import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import getDecodeDescription from "../../services/decodeText";
import { propsNamesAdapter } from "../../services/propsNamesAdapter";
import StepsForm from "../StepsForm/StepsForm";
import {
  checkIsValidFormSteps,
  updateErrorsFormSteps,
} from "../../services/manageValidation";
import FirstStep from "./FirstStep";
import SecondStep from "./SecondStep";
import ThirdStep from "./ThirdStep";
import { ModalConsumer } from "../../common/ModalContext";
import ModalAdditionResult from "../../components/ModalAdditionResult/ModalAdditionResult";
import styles from "./GreenSpotForm.module.scss";

const GreenSpotForm = ({ handleSubmit, spotToEdit, stepStatus, type }) => {
  useEffect(() => {
    if (spotToEdit.type && spotToEdit.type !== "greenSpot") {
      return;
    }

    if (!spotToEdit) {
      return;
    }
    setName(spotToEdit.name);
    setCountries(spotToEdit.countries);
    spotToEdit.address && setAddress(spotToEdit.address);
    spotToEdit.key && setKey(spotToEdit.key);
    spotToEdit.links && !spotToEdit.links.length
      ? setLinks([{}])
      : setLinks(spotToEdit.links);
    spotToEdit.email && setEmail(spotToEdit.email);
    setGreenSpotFields(spotToEdit.fields);
    spotToEdit.relatedOrganizations && !spotToEdit.relatedOrganizations.length
      ? setRelatedOrganizations([{}])
      : setRelatedOrganizations(spotToEdit.relatedOrganizations);
    spotToEdit.photos && !spotToEdit.photos.length
      ? setPhotos([{}])
      : setPhotos(spotToEdit.photos);
    spotToEdit.videos && !spotToEdit.videos.length
      ? setVideos([{}])
      : setVideos(spotToEdit.videos);
    spotToEdit.titleSeo && setTitleSeo(spotToEdit?.titleSeo);
    spotToEdit.descriptionSeo && setDescriptionSeo(spotToEdit?.descriptionSeo);
    spotToEdit.timeline && !spotToEdit.timeline.length
      ? setTimeline([{}])
      : setTimeline(
          spotToEdit.timeline?.map((item) =>
            Object.keys(item).length
              ? { ...item, text: getDecodeDescription(item.text) }
              : {}
          )
        );
    spotToEdit?.problem && !spotToEdit?.problem?.length
      ? setProblem([{}])
      : setProblem(
          spotToEdit?.problem?.map((item) =>
            Object.keys(item).length
              ? { ...item, text: getDecodeDescription(item.text) }
              : {}
          )
        );
    spotToEdit?.solutions && !spotToEdit.solutions.length
      ? setSolutions([{}])
      : setSolutions(
          spotToEdit?.solutions?.map((item) =>
            Object.keys(item).length
              ? { ...item, text: getDecodeDescription(item.text) }
              : {}
          )
        );
  }, [spotToEdit]);

  ///USE CUSTOM HOOK
  const [countries, setCountries] = useState([]);
  const [greenSpotFields, setGreenSpotFields] = useState([]);
  const [validationError, setValidationError] = useState([]);
  const [photos, setPhotos] = useState([{}]);
  const [relatedOrganizations, setRelatedOrganizations] = useState([{}]);
  const [name, setName] = useState("");
  const [links, setLinks] = useState([{}]);
  const [key, setKey] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [problem, setProblem] = useState([{}]);
  const [solutions, setSolutions] = useState([{}]);
  const [timeline, setTimeline] = useState([{}]);
  const [videos, setVideos] = useState([{}]);
  const [titleSeo, setTitleSeo] = useState("");
  const [descriptionSeo, setDescriptionSeo] = useState("");

  const changeValidationError = (filterType) => {
    // MOVE TO SERVICES?
    setValidationError(
      validationError.filter((errorItem) => errorItem !== filterType)
    );
  };

  const removeEmptyObject = (arr) => {
    const result = arr?.filter((element) => {
      if (Object.keys(element).length !== 0) return true;
      return false;
    });
    return result;
  };

  const manageNextStep = (step, toggleModal) => {
    const state = {
      name,
      countries,
      address,
      greenSpotFields,
      email,
      photos,
      relatedOrganizations,
      links,
      problem,
      solutions,
      timeline,
      videos,
      type: type,
    };

    if (!checkIsValidFormSteps({ state, step })) {
      updateErrorsFormSteps({
        state,
        step,
        setValidationError,
      });
      return false;
    }
    if (step === 3 && checkIsValidFormSteps({ state, step })) {
      handleSubmit({
        toggleModal,
        name,
        author: spotToEdit?.author,
        countries,
        address,
        greenSpotFields,
        type: type,
        status: "onreview",
        photos: removeEmptyObject(photos),
        relatedOrganizations: removeEmptyObject(relatedOrganizations),
        links: removeEmptyObject(links),
        problem: removeEmptyObject(problem),
        solutions: removeEmptyObject(solutions),
        timeline: removeEmptyObject(timeline),
        videos: removeEmptyObject(videos),
        titleSeo,
        descriptionSeo,
        key: spotToEdit?.key,
        _id: spotToEdit?._id,
        setName,
        setCountries,
        setAddress,
        setGreenSpotFields,
        setPhotos,
        setRelatedOrganizations,
        setLinks,
        setProblem,
        setSolutions,
        setTimeline,
        setVideos,
        setValidationError,
        setTitleSeo,
        setDescriptionSeo,
        setHotSpotFields: () => {},
      });
    }
    return true;
  };
  const changeMultipleFieldsObject = (
    itemName,
    itemValue,
    index,
    setState,
    items
  ) => {
    const updatedItems = items;
    updatedItems[index][propsNamesAdapter(itemName)] = itemValue;
    if (Object.values(items[index]).every((i) => i === "")) {
      if (items.length === 1) {
        updatedItems[0] = {};
      } else {
        updatedItems.splice(index, 1);
      }
    }

    setState(updatedItems);
  };

  const addFieldObject = (setState, items) => {
    const updatedItems = [...items];
    updatedItems.push({});
    setState(updatedItems);
  };

  const firstStepState = {
    countries,
    setCountries,
    greenSpotFields,
    setGreenSpotFields,
    name,
    setName,
    address,
    setAddress,
    problem,
    setProblem,
    solutions,
    setSolutions,
    addFieldObject,
    validationError,
    setValidationError,
    changeMultipleFieldsObject,
    changeValidationError,
    type,
  };

  const secondStepState = {
    timeline,
    setTimeline,
    addFieldObject,
    validationError,
    setValidationError,
    changeMultipleFieldsObject,
    changeValidationError,
    type,
  };

  const thirdStepState = {
    photos,
    setPhotos,
    links,
    setLinks,
    videos,
    setVideos,
    relatedOrganizations,
    setRelatedOrganizations,
    validationError,
    setValidationError,
    changeMultipleFieldsObject,
    changeValidationError,
    addFieldObject,
    titleSeo,
    setTitleSeo,
    descriptionSeo,
    setDescriptionSeo,
    type,
  };
  return (
    <div className={styles.container}>
      <StepsForm
        type={type}
        manageNextStep={manageNextStep}
        firstStepFields={<FirstStep state={firstStepState} />}
        secondStepFields={<SecondStep state={secondStepState} />}
        thirdStepFields={<ThirdStep state={thirdStepState} />}
        stepsAmount={3}
        resetStep={stepStatus}
      />
      <ModalConsumer>
        {({ isOpen, toggleModal, modalId }) => {
          return (
            <>
              {modalId === "organization" && (
                <ModalAdditionResult
                  toggleModal={toggleModal}
                  isOpen={isOpen}
                  content="You've added a spot"
                />
              )}

              {modalId === "wrongAddress" && (
                <ModalAdditionResult
                  toggleModal={toggleModal}
                  isOpen={isOpen}
                  content="Address is wrong. Please change the address and try again"
                />
              )}
            </>
          );
        }}
      </ModalConsumer>
    </div>
  );
};

GreenSpotForm.propTypes = {
  lng: PropTypes.object,
  handleSubmit: PropTypes.func,
  type: PropTypes.string,
  spotToEdit: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  clearAll: PropTypes.func,
  isSpotEditor: PropTypes.bool,
};

export default GreenSpotForm;
